import { useTranslation } from 'react-i18next';

import ArrowDownCircleFill from '@/assets/icons/arrow_down_circle_fill.svg?component';
import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import Tooltip from '@/components/atoms/Tooltip';
import ContentBlock from '@/components/molecules/ContentBlock';
import { MetricCard } from '@/components/molecules/MetricCard';
import QualityBasisBadge from '@/components/molecules/QualityBasisBadge';
import type { RetryProps } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { formatDateDisplay } from '@/utils/date';
import { formatNumberByLocale, parseLocale } from '@/utils/localeFormatters';

import styles from './cardParts.module.scss';

const PERCENT_DECIMALS = 1;
const NO_VALUE = '-';
const TOOLTIP_WIDTH = 450;

interface QualityEstimateCardProps {
    /** Share of the harvest expected to be downgraded, as a fraction. */
    downgradeRate: number | undefined;
    /** Latest welfare day for the estimate (`YYYY-MM-DD`), if any. */
    lastMeasurement: string | null | undefined;
    isLoading: boolean;
    /** Set when the estimate failed to load: the error state with Try again replaces it. */
    loadError?: RetryProps | null;
}

/** The expected harvest downgrade, and the badge saying it is estimated from wounds. */
export default function QualityEstimateCard(props: QualityEstimateCardProps) {
    const { downgradeRate, lastMeasurement, isLoading, loadError } = props;
    const { t, i18n } = useTranslation();
    const locale = parseLocale(i18n.language);

    // A zero rate is shown as missing, as it always has been: the welfare model reports no
    // estimate as 0 rather than null.
    const value = downgradeRate
        ? formatNumberByLocale(downgradeRate * 100, locale, PERCENT_DECIMALS, NO_VALUE)
        : NO_VALUE;

    const lastMeasurementLabel =
        lastMeasurement != null && lastMeasurement !== ''
            ? formatDateDisplay(lastMeasurement)
            : null;

    return (
        <ContentBlock
            title={t('harvestReport.quality.title')}
            spacing="none"
            headerActions={<QualityBasisBadge />}>
            {loadError ? (
                <SomethingHappened {...loadError} />
            ) : (
                <>
                    <div className={styles.lastMeasurementRow}>
                        <span>{t('common.labels.lastMeasurement')}</span>
                        {isLoading ? (
                            <SkeletonBox
                                width={150}
                                height={24}
                                borderRadius={8}
                                className={styles.lastMeasurementSkeleton}
                            />
                        ) : (
                            <span>{lastMeasurementLabel ?? NO_VALUE}</span>
                        )}
                    </div>
                    <Tooltip
                        content={t('harvestReport.common.estDowngradeTooltip')}
                        maxWidth={TOOLTIP_WIDTH}>
                        <MetricCard
                            title={t('common.labels.estimatedDowngrade')}
                            value={value}
                            suffix={value === NO_VALUE ? undefined : '%'}
                            icon={ArrowDownCircleFill}
                            tone="danger"
                            isLoading={isLoading}
                            framed="tinted"
                        />
                    </Tooltip>
                </>
            )}
        </ContentBlock>
    );
}
